import { DataItem } from '@pn/core/domain/data';
import { IntelDataItem } from '@pn/core/domain/intel/intel-workspace-item.ts';
import { DateTimeString } from '@pn/core/domain/types.ts';
import { generateTemporaryId } from '@pn/core/utils/id.ts';
import {
  compressAndEncode,
  decodeAndDecompress,
} from '@pn/services/utils/compression.ts';
import dayjs from 'dayjs';
import { isNil } from 'lodash-es';

export function getDateRangeFromReportFilters(
  filters: IntelReportFilters
): [DateTimeString, DateTimeString] {
  const { daysAgoWindow, customStaticStartDate, customStaticEndDate } = filters;
  if (isNil(daysAgoWindow)) {
    if (customStaticStartDate && customStaticEndDate) {
      return [customStaticStartDate, customStaticEndDate];
    } else {
      console.warn(
        'Invalid date data provided for the report. Using default value'
      );
      return ['1970-01-01', '1970-01-01'];
    }
  } else {
    const yesterday = dayjs().subtract(1, 'day');
    const yesterdayFormatted = yesterday.format('YYYY-MM-DD');
    return [
      yesterday.subtract(daysAgoWindow - 1, 'days').format('YYYY-MM-DD'),
      yesterdayFormatted,
    ];
  }
}

export const daysAgoWindowOptions = [7, 30, 90, 365];

export type IntelReportFilters = {
  daysAgoWindow: number | null;
  customStaticStartDate: DateTimeString | null;
  customStaticEndDate: DateTimeString | null;
  type: IntelReportType;
  name: string;
  customProductionMonth: DateTimeString | null;
  loadMostRecentProductionMonth: boolean;
  geoJSONGeometry: GeoJSON.Geometry | null;
  areaId: string | null;
  formation: string | null;
  companyBrandId: string | null;
  fieldName: string | null;
};

export type ProcessedIntelReportFilters = Omit<
  IntelReportFilters,
  'timespan'
> & {
  companyBrandName?: string;
  productionMonthToConsider?: DateTimeString;
  startDate: DateTimeString;
  endDate: DateTimeString;
  historicalDataStartDate: DateTimeString;
};

/**
 * @param filters
 * @param companyBrandIdAndNameRecords
 *
 * This function dynamically builds the queryable filters based on the provided filters.
 * (e.g. if the date window is set to 7 days, it will calculate the start and end date)
 */
export function processIntelReportFilters(
  filters: IntelReportFilters,
  companyBrandIdAndNameRecords: Record<string, string>
): ProcessedIntelReportFilters {
  const [startDate, endDate] = getDateRangeFromReportFilters(filters);
  const reversedRecords = Object.entries(companyBrandIdAndNameRecords).reduce(
    (acc, [key, value]) => {
      acc[value] = key;
      return acc;
    },
    {} as Record<string, string>
  );

  const historicalDataStartDate = dayjs(startDate)
    .subtract(3, 'month')
    .startOf('month')
    .format('YYYY-MM-DD');

  return {
    ...filters,
    startDate,
    endDate,
    companyBrandName: isNil(filters.companyBrandId)
      ? undefined
      : reversedRecords[filters.companyBrandId],
    productionMonthToConsider: dayjs()
      .subtract(2, 'month')
      .startOf('month')
      .format('YYYY-MM-DD'),
    historicalDataStartDate,
  };
}

export const enum IntelReportType {
  Area = 'Area',
  Company = 'Company',
  Field = 'Field',
  Formation = 'Formation',
}

export const IntelReportTypes = [
  IntelReportType.Area,
  IntelReportType.Company,
  IntelReportType.Field,
  IntelReportType.Formation,
];

export type IntelReport = {
  id: string;
  filters: IntelReportFilters;
  emailSchedule: 'weekly' | 'monthly' | null;
  encodedFilters: string; // stored here for optimization
  isPinned: boolean;
  isArchived: boolean;
};

export function isEmailSchedule(
  arg: unknown
): arg is IntelReport['emailSchedule'] {
  return arg === 'weekly' || arg === 'monthly' || arg === null;
}

export type IntelReportData = {
  reportId: string;
  monthlySpuddedLicenses: DataItem[];
  monthlyLicenses: DataItem[];
  licencesYearOverYear: IntelDataItem[];
  spudsYearOverYear: IntelDataItem[];
  production: DataItem[];
  isLoading: boolean;
  isLoadingYearOverYear: boolean;
  isLoadingMonthlyLicenses: boolean;
  isLoadingMonthlySpuddedLicenses: boolean;
  isLoadingProduction: boolean;
};

export type IntelReportWithData = IntelReport & IntelReportData;

export function compressAndEncodeReportSettings(settings: IntelReportFilters) {
  return compressAndEncode(settings);
}

export function decodeAndDecompressReportSettings(
  encodedString: string
): IntelReportFilters {
  return decodeAndDecompress<IntelReportFilters>(encodedString);
}

export function createIntelReport(
  filters: IntelReportFilters,
  options?: {
    isPinned: boolean;
    emailSchedule?: IntelReport['emailSchedule'];
  }
): IntelReport {
  return {
    id: generateTemporaryId(),
    filters,
    encodedFilters: compressAndEncodeReportSettings(filters),
    emailSchedule: options?.emailSchedule ?? null,
    isPinned: false,
    isArchived: false,
    ...options,
  };
}

export function createIntelReportData(reportId: string): IntelReportData {
  return {
    reportId,
    monthlySpuddedLicenses: [],
    monthlyLicenses: [],
    licencesYearOverYear: [],
    spudsYearOverYear: [],
    production: [],
    isLoading: false,
    isLoadingMonthlyLicenses: false,
    isLoadingYearOverYear: false,
    isLoadingMonthlySpuddedLicenses: false,
    isLoadingProduction: false,
  };
}
